/**
 * Memory 系统集成模块
 * 为 ContextBuilder 提供记忆增强功能
 */

import { logger } from '../../utils/logger';
import { ContextItem, ContextType } from './types';
import { v4 as uuidv4 } from 'uuid';

// Memory 系统接口（简化版）
export interface IMemoryClient {
  search(query: string, options?: SearchOptions): Promise<SearchResult[]>;
  add(content: string, metadata?: any): Promise<void>;
  getRelated(content: string, limit?: number): Promise<SearchResult[]>;
}

export interface SearchOptions {
  limit?: number;
  minScore?: number;
  filter?: Record<string, any>;
}

export interface SearchResult {
  id: string;
  content: string;
  score: number;
  metadata?: Record<string, any>;
}

// 适配器接口
export interface ITaskMemoryAdapter {
  suggestTask(description: string): Promise<TaskSuggestion>;
  getTaskPatterns(taskType: string): Promise<TaskPattern[]>;
  getFrequentChallenges(taskType: string): Promise<string[]>;
}

export interface IAgentMemoryAdapter {
  recommendAgent(taskDescription: string): Promise<AgentRecommendation>;
  getAgentPerformance(agentId: string): Promise<AgentPerformance>;
}

export interface IUserPreferenceAdapter {
  getWorkPattern(): Promise<WorkPattern>;
  getCodingStyle(): Promise<CodingStyle>;
  getToolPreferences(): Promise<ToolPreference[]>;
}

// 数据结构定义
export interface TaskSuggestion {
  suggestedTasks: Array<{
    name: string;
    type: string;
    priority: string;
    estimatedTime: string;
    reason: string;
  }>;
  patterns: TaskPattern[];
}

export interface TaskPattern {
  type: string;
  avgCompletionTime: string;
  commonChallenges: string[];
  bestPractices: string[];
}

export interface AgentRecommendation {
  agentId: string;
  confidence: number;
  reason: string;
  alternatives: Array<{
    agentId: string;
    confidence: number;
  }>;
}

export interface AgentPerformance {
  agentId: string;
  successRate: number;
  avgExecutionTime: number;
  taskTypes: string[];
}

export interface WorkPattern {
  productiveHours: string[];
  avgTasksPerDay: number;
  preferredTaskTypes: string[];
}

export interface CodingStyle {
  preferredLanguages: string[];
  codePatterns: string[];
  commonLibraries: string[];
}

export interface ToolPreference {
  tool: string;
  frequency: number;
  contexts: string[];
}

/**
 * Memory 集成配置
 */
export interface MemoryIntegrationConfig {
  client?: IMemoryClient;
  taskAdapter?: ITaskMemoryAdapter;
  agentAdapter?: IAgentMemoryAdapter;
  preferenceAdapter?: IUserPreferenceAdapter;
  enabled?: boolean;
}

/**
 * Memory 集成助手
 * 提供记忆相关的上下文增强功能
 */
export class MemoryIntegration {
  private config: MemoryIntegrationConfig;
  
  constructor(config: MemoryIntegrationConfig = {}) {
    this.config = config;
  }
  
  /**
   * 检查是否启用记忆系统
   */
  isEnabled(): boolean {
    return this.config.enabled === true && !!this.config.client;
  }
  
  /**
   * 搜索相关记忆
   */
  async searchMemory(query: string): Promise<ContextItem[]> {
    if (!this.config.client) return [];
    
    try {
      const results = await this.config.client.search(query, {
        limit: 10,
        minScore: 0.7
      });
      
      return results.map(result => ({
        id: uuidv4(),
        type: ContextType.KNOWLEDGE,
        content: {
          text: result.content,
          source: 'memory',
          confidence: result.score
        },
        source: 'memory',
        relevance: result.score,
        timestamp: Date.now(),
        metadata: {
          memoryId: result.id,
          ...result.metadata
        }
      }));
    } catch (error) {
      logger.error('Memory search failed:', error);
      return [];
    }
  }
  
  /**
   * 获取任务建议
   */
  async getTaskSuggestions(task: any): Promise<ContextItem | null> {
    if (!this.config.taskAdapter) return null;
    
    try {
      const suggestion = await this.config.taskAdapter.suggestTask(task.description);
      const patterns = await this.config.taskAdapter.getTaskPatterns(task.type);
      const challenges = await this.config.taskAdapter.getFrequentChallenges(task.type);
      
      return {
        id: uuidv4(),
        type: ContextType.WORKFLOW,
        content: {
          suggestions: suggestion.suggestedTasks,
          patterns,
          challenges,
          insights: this.generateTaskInsights(patterns, challenges)
        },
        source: 'task-memory',
        relevance: 0.9,
        timestamp: Date.now(),
        metadata: {
          taskType: task.type,
          priority: task.priority
        }
      };
    } catch (error) {
      logger.error('Failed to get task suggestions:', error);
      return null;
    }
  }
  
  /**
   * 获取 Agent 推荐
   */
  async getAgentRecommendation(task: any): Promise<ContextItem | null> {
    if (!this.config.agentAdapter) return null;
    
    try {
      const recommendation = await this.config.agentAdapter.recommendAgent(task.description);
      const performance = await this.config.agentAdapter.getAgentPerformance(
        recommendation.agentId
      );
      
      return {
        id: uuidv4(),
        type: ContextType.WORKFLOW,
        content: {
          recommendation,
          performance,
          reasoning: `推荐 ${recommendation.agentId}，成功率 ${(performance.successRate * 100).toFixed(1)}%，${recommendation.reason}`
        },
        source: 'agent-memory',
        relevance: recommendation.confidence,
        timestamp: Date.now(),
        metadata: {
          agentId: recommendation.agentId
        }
      };
    } catch (error) {
      logger.error('Failed to get agent recommendation:', error);
      return null;
    }
  }
  
  /**
   * 获取用户偏好
   */
  async getUserPreferences(): Promise<ContextItem | null> {
    if (!this.config.preferenceAdapter) return null;
    
    try {
      const [workPattern, codingStyle, toolPreferences] = await Promise.all([
        this.config.preferenceAdapter.getWorkPattern(),
        this.config.preferenceAdapter.getCodingStyle(),
        this.config.preferenceAdapter.getToolPreferences()
      ]);
      
      return {
        id: uuidv4(),
        type: ContextType.METADATA,
        content: {
          workPattern,
          codingStyle,
          toolPreferences,
          insights: this.generatePreferenceInsights(workPattern, codingStyle)
        },
        source: 'user-preference',
        relevance: 0.8,
        timestamp: Date.now()
      };
    } catch (error) {
      logger.error('Failed to get user preferences:', error);
      return null;
    }
  }
  
  /**
   * 获取历史相关的记忆
   */
  async getHistoricalMemory(history: any[]): Promise<ContextItem[]> {
    if (!this.config.client || history.length === 0) return [];
    
    try {
      const insights: ContextItem[] = [];
      
      // 只查看最近的几条历史
      for (const item of history.slice(-3)) {
        const similar = await this.config.client.getRelated(
          JSON.stringify(item),
          3
        );
        
        if (similar.length > 0) {
          insights.push({
            id: uuidv4(),
            type: ContextType.HISTORY,
            content: {
              current: item,
              similar: similar.map(s => ({
                content: s.content,
                similarity: s.score
              }))
            },
            source: 'historical-memory',
            relevance: 0.7,
            timestamp: Date.now()
          });
        }
      }
      
      return insights;
    } catch (error) {
      logger.error('Failed to get historical memory:', error);
      return [];
    }
  }
  
  /**
   * 保存上下文到记忆
   */
  async saveToMemory(context: any, metadata: Record<string, any>): Promise<void> {
    if (!this.config.client) return;
    
    try {
      const summary = this.buildContextSummary(context);
      await this.config.client.add(summary, {
        category: 'context',
        ...metadata
      });
    } catch (error) {
      logger.error('Failed to save to memory:', error);
    }
  }
  
  /**
   * 生成任务洞察
   */
  private generateTaskInsights(patterns: TaskPattern[], challenges: string[]): string {
    const insights: string[] = [];
    
    if (patterns.length > 0) {
      const avgTime = patterns[0].avgCompletionTime;
      insights.push(`此类任务通常需要 ${avgTime}`);
    }
    
    if (challenges.length > 0) {
      insights.push(`常见挑战：${challenges.slice(0, 3).join('、')}`);
    }
    
    if (patterns[0]?.bestPractices.length > 0) {
      insights.push(`最佳实践：${patterns[0].bestPractices[0]}`);
    }
    
    return insights.join('。');
  }
  
  /**
   * 生成偏好洞察
   */
  private generatePreferenceInsights(
    workPattern: WorkPattern,
    codingStyle: CodingStyle
  ): string {
    const insights: string[] = [];
    
    if (workPattern.productiveHours.length > 0) {
      insights.push(`高效时段：${workPattern.productiveHours.join('、')}`);
    }
    
    if (codingStyle.preferredLanguages.length > 0) {
      insights.push(`偏好语言：${codingStyle.preferredLanguages.join('、')}`);
    }
    
    return insights.join('；');
  }
  
  /**
   * 构建上下文摘要
   */
  private buildContextSummary(context: any): string {
    const parts: string[] = [];
    
    if (context.task) {
      parts.push(`任务：${context.task.name || context.task.description}`);
    }
    
    if (context.query) {
      parts.push(`查询：${context.query}`);
    }
    
    if (context.items) {
      parts.push(`包含 ${context.items.length} 个上下文项`);
    }
    
    return parts.join('；');
  }
}